import { ApiError, type VitalsResult } from '../../api/client.js';
import { h, type View } from './dom.js';
import './scan-card.css';

type ScanUnit = {
  deviceId: string;
  name: string;
  available: boolean;
  state: 'idle' | 'queued' | 'video' | 'voice' | 'finishing' | 'failed' | 'cancelled';
  quality?: 'ok' | 'no_face' | 'more_light' | 'hold_still' | 'move_closer' | 'signal_insufficient';
  progress?: number;
  actionId?: string;
  startedAt?: number;
};
type ScanSnapshot = { result: VitalsResult | null; history?: VitalsResult[]; units?: ScanUnit[] };
type ScanApi = {
  getVitalsLatest?: (signal?: AbortSignal) => Promise<ScanSnapshot>;
  startVitalsScan?: (deviceId: string, signal?: AbortSignal) => Promise<{ actionId: string; state: 'queued' }>;
};
const DISCLAIMER = 'estimación de bienestar, no es un diagnóstico';
const ACTIVE_STATES = new Set<ScanUnit['state']>(['queued', 'video', 'voice', 'finishing']);

/** Reads account results; the only capture action is an explicit click on a unit. */
export function createScanCard(api: ScanApi): View {
  let active = true;
  let loading = false;
  let reloadQueued = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let snapshot: ScanSnapshot | null = null;
  let selectedId = '';
  let submittingId: string | null = null;
  let readVersion = 0;
  let readError = '';
  let actionError = '';
  let resultKey = '';
  let historyKey = '';
  let unitsKey = '';
  const lifetime = new AbortController();
  const queuedActions = new Map<string, string>();
  const root = h('section', { class: 'card scan-card', 'aria-label': 'Escaneo de bienestar' });
  const title = h('h2', { class: 'card-title' }, ['Escaneo de bienestar']);
  const unitName = h('span', { class: 'scan-unit-name' });
  const select = h('select', { class: 'select', 'aria-label': 'Unidad para el escaneo' });
  const chooseUnit = h('label', { class: 'field scan-unit-select', hidden: true }, [h('span', {}, ['Unidad']), select]);
  const start = h('button', { type: 'button', class: 'btn primary', disabled: true }, ['Escanear']);
  const unitRow = h('div', { class: 'scan-unit-row' }, [unitName, chooseUnit, start]);
  const status = h('p', { class: 'scan-status', role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' }, ['Cargando escaneos…']);
  const guidance = h('p', { class: 'scan-guidance', hidden: true });
  const progress = h('progress', { class: 'scan-progress', max: '1', 'aria-label': 'Progreso de la captura del rostro', hidden: true });
  const result = h('div', { class: 'scan-result', 'aria-live': 'polite', 'aria-atomic': 'true' });
  const error = h('p', { class: 'auth-error', role: 'alert', hidden: true });
  const retry = h('button', { type: 'button', class: 'btn ghost', hidden: true }, ['Reintentar conexión']);
  const history = h('details', { class: 'scan-history', hidden: true });
  const historyList = h('ol', { class: 'scan-history-list' });
  history.append(h('summary', {}, ['Últimos escaneos']), historyList);
  root.append(title, unitRow, status, guidance, progress, result, error, retry,
    h('p', { class: 'scan-disclaimer' }, [DISCLAIMER]), history);

  function currentUnit(): ScanUnit | undefined { return snapshot?.units?.find(unit => unit.deviceId === selectedId); }
  function results(): VitalsResult[] {
    const values = snapshot?.history ?? (snapshot?.result ? [snapshot.result] : []);
    return values.filter(item => !selectedId || item.deviceId === selectedId).slice(0, 5);
  }

  function render(): void {
    const units = snapshot?.units ?? [];
    if (!units.some(unit => unit.deviceId === selectedId)) selectedId = units.find(unit => unit.available)?.deviceId ?? units[0]?.deviceId ?? '';
    const nextUnitsKey = JSON.stringify(units.map(unit => [unit.deviceId, unit.name]));
    if (unitsKey !== nextUnitsKey) {
      unitsKey = nextUnitsKey;
      select.replaceChildren(...units.map(unit => h('option', { value: unit.deviceId }, [unit.name])));
    }
    select.value = selectedId;
    chooseUnit.hidden = units.length < 2;
    unitName.hidden = units.length > 1;
    const unit = currentUnit();
    unitName.textContent = unit?.name ?? 'Tu unidad ASIS';
    const state = unit?.state;
    const isSubmitting = submittingId === selectedId && submittingId !== null;
    const isQueued = queuedActions.has(selectedId);
    const busy = Boolean(state && ACTIVE_STATES.has(state)) || isSubmitting || isQueued;
    const latest = results()[0] ?? null;
    const poorSignal = Boolean(latest && validPulse(latest) === null);
    start.textContent = isSubmitting ? 'Solicitando…' : busy ? 'Escaneo en curso' : poorSignal || state === 'failed' ? 'Repetir' : 'Escanear';
    start.disabled = !unit?.available || busy || Boolean(readError) || !api.startVitalsScan;
    select.disabled = submittingId !== null;
    if (isSubmitting) status.textContent = 'Solicitando el escaneo a tu unidad…';
    else if (state === 'video') status.textContent = 'Midiendo el pulso…';
    else if (state === 'voice') status.textContent = 'Sigue la indicación de voz de tu unidad.';
    else if (state === 'finishing') status.textContent = 'Analizando la señal…';
    else if (state === 'queued' || isQueued) status.textContent = 'Solicitud en espera. Aún no ha comenzado la captura.';
    else if (state === 'failed') status.textContent = 'No se pudo completar el escaneo. Puedes repetirlo.';
    else if (state === 'cancelled') status.textContent = 'Escaneo cancelado. Puedes volver a empezar.';
    else if (!snapshot) status.textContent = 'Cargando escaneos…';
    else if (!unit) status.textContent = 'No hay unidades disponibles para escanear.';
    else if (!unit.available) status.textContent = 'Unidad no disponible. Comprueba su conexión para escanear.';
    else status.textContent = 'Lista para escanear';
    if (readError) status.textContent = 'No se puede confirmar el estado de la unidad.';

    guidance.textContent = state === 'video'
      ? unit?.quality === 'no_face' ? 'Coloca el rostro dentro de la guía de tu unidad.'
        : unit?.quality === 'move_closer' ? 'Acércate un poco a tu unidad y mira al frente.'
          : unit?.quality && unit.quality !== 'ok' ? 'Acércate a la luz y quédate quieto.'
            : 'Mira al frente y quédate quieto hasta que termine.'
      : state === 'voice' ? 'Di «aaaa» cuando tu unidad te lo pida.' : '';
    guidance.hidden = !guidance.textContent;
    // A missing progress value remains indeterminate: time passing is not evidence of capture.
    progress.hidden = state !== 'video';
    if (typeof unit?.progress === 'number' && Number.isFinite(unit.progress) && unit.progress >= 0 && unit.progress <= 1) progress.value = unit.progress;
    else progress.removeAttribute('value');

    const nextResultKey = JSON.stringify([latest, busy]);
    if (resultKey !== nextResultKey) {
      resultKey = nextResultKey;
      result.replaceChildren();
      if (latest) {
        const pulse = validPulse(latest);
        result.append(h('p', { class: 'scan-result-label' }, [busy ? 'Último resultado' : 'Resultado del escaneo']));
        if (pulse !== null) {
          result.append(h('div', { class: 'scan-pulse', 'aria-label': `Pulso: ${pulse} latidos por minuto` }, [
            h('strong', {}, [String(pulse)]), h('span', {}, ['latidos/min']),
          ]), h('p', { class: 'scan-confidence' }, ['Señal suficiente']));
        } else result.append(h('p', { class: 'scan-insufficient' }, ['Señal insuficiente']),
          h('p', { class: 'scan-guidance' }, ['Acércate a la luz y quédate quieto. Podemos repetir el escaneo.']));
        result.append(timeLabel(latest.capturedAt));
      } else result.append(h('p', { class: 'scan-empty' }, ['Tu pulso aparecerá aquí cuando completes un escaneo con señal suficiente.']));
    }
    const recent = results();
    const nextHistoryKey = JSON.stringify(recent);
    if (historyKey !== nextHistoryKey) {
      historyKey = nextHistoryKey;
      history.hidden = recent.length === 0;
      historyList.replaceChildren(...recent.map(item => {
        const pulse = validPulse(item);
        return h('li', { class: 'scan-history-row' }, [timeLabel(item.capturedAt),
          h('span', {}, [pulse === null ? 'Señal insuficiente' : `${pulse} latidos/min · Señal suficiente`]),
        ]);
      }));
    }
    error.textContent = actionError || readError;
    error.hidden = !error.textContent;
    retry.hidden = !readError;
  }

  async function refresh(): Promise<void> {
    if (!active || !api.getVitalsLatest) return;
    if (loading) { reloadQueued = true; return; }
    if (timer !== undefined) { clearTimeout(timer); timer = undefined; }
    loading = true;
    const version = readVersion;
    try {
      const response = await api.getVitalsLatest(lifetime.signal);
      if (!active || version !== readVersion) return;
      snapshot = response;
      readError = '';
      // Only a read begun after POST acknowledgement can release the local duplicate guard.
      for (const id of queuedActions.keys()) if (response.units?.some(unit => unit.deviceId === id)) queuedActions.delete(id);
      render();
    } catch {
      if (!active || lifetime.signal.aborted) return;
      readError = 'No se pudieron actualizar los escaneos. Reintenta la conexión.';
      render();
    } finally {
      loading = false;
      if (active) {
        if (reloadQueued) { reloadQueued = false; void refresh(); }
        else timer = setTimeout(() => { void refresh(); }, queuedActions.size || snapshot?.units?.some(unit => ACTIVE_STATES.has(unit.state)) ? 1000 : 5000);
      }
    }
  }

  start.addEventListener('click', async () => {
    const unit = currentUnit();
    if (!active || start.disabled || !unit || !api.startVitalsScan) return;
    submittingId = unit.deviceId;
    actionError = '';
    readVersion++;
    render();
    try {
      const response = await api.startVitalsScan(unit.deviceId, lifetime.signal);
      if (!active) return;
      queuedActions.set(unit.deviceId, response.actionId);
    } catch (cause) {
      if (!active) return;
      actionError = cause instanceof ApiError ? cause.message : 'No se pudo solicitar el escaneo. Comprueba la conexión y vuelve a intentarlo.';
    } finally {
      submittingId = null;
      readVersion++;
      if (active) { render(); void refresh(); }
    }
  });
  select.addEventListener('change', () => { selectedId = select.value; actionError = ''; render(); });
  retry.addEventListener('click', () => { void refresh(); });
  if (api.getVitalsLatest) void refresh();
  else { root.hidden = true; root.replaceChildren(); }
  return {
    el: root,
    update() { void refresh(); },
    destroy() {
      active = false;
      lifetime.abort();
      if (timer !== undefined) clearTimeout(timer);
      queuedActions.clear();
    },
  };
}

function validPulse(result: VitalsResult): number | null {
  return result.status === 'complete' && typeof result.hrBpm === 'number' && Number.isFinite(result.hrBpm)
    && result.hrBpm >= 40 && result.hrBpm <= 180 ? Math.round(result.hrBpm) : null;
}

function timeLabel(timestamp: number): HTMLElement {
  const date = new Date(timestamp);
  if (!Number.isFinite(timestamp) || !Number.isFinite(date.getTime())) return h('span', { class: 'scan-time' }, ['Hora no disponible']);
  return h('time', { class: 'scan-time', datetime: date.toISOString() }, [
    new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(date),
  ]);
}
